import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GridLayout, { WidthProvider, type Layout } from 'react-grid-layout';
import { Check, CheckCircle2, LayoutGrid, ListTodo, Pencil, Plus, X } from 'lucide-react';
import type { Dashboard as DashboardType, Task, WidgetDefinition } from '@itr/shared';
import { formatRelative } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useRealtimeEvent } from '../api/realtime';
import { WidgetRenderer, type WidgetInstance } from '../components/WidgetRenderer';
import { Badge, Drawer, EmptyState, ErrorState, Skeleton, useFetch, useToast } from '../components/ui';
import { TaskEditForm, TaskForm, type TaskAssignee } from './Tasks';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const Grid = WidthProvider(GridLayout);

type CatalogWidget = WidgetDefinition & { pluginId: string; fullId: string };

/**
 * The dashboard.
 *
 * Layout is stored per dashboard record, so a user's arrangement follows them
 * across devices. Edit mode is explicit rather than always-on: on a NOC screen
 * an accidental drag that silently rearranges the wall is worse than one extra
 * click to start editing.
 */
export function Dashboard() {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [dashboard, setDashboard] = useState<DashboardType | null>(null);
  const [dirty, setDirty] = useState(false);

  const { data: loaded, loading, error, reload } = useFetch(
    () => api.get<DashboardType | null>('/api/dashboards/default'),
    [],
  );
  const { data: catalog } = useFetch(() => api.get<CatalogWidget[]>('/api/widgets'), []);

  useEffect(() => {
    if (loaded) setDashboard(loaded);
  }, [loaded]);

  const definitions = useMemo(() => new Map((catalog ?? []).map((w) => [w.fullId, w])), [catalog]);

  const layout = useMemo<Layout[]>(
    () =>
      (dashboard?.widgets ?? []).map((w) => ({
        i: w.instanceId,
        x: w.layout.x,
        y: w.layout.y,
        w: w.layout.w,
        h: w.layout.h,
        minW: definitions.get(w.widgetId)?.minSize?.w ?? 2,
        minH: definitions.get(w.widgetId)?.minSize?.h ?? 2,
      })),
    [dashboard, definitions],
  );

  const onLayoutChange = useCallback(
    (next: Layout[]) => {
      if (!editing || !dashboard) return;
      setDashboard((current) => {
        if (!current) return current;
        const byId = new Map(next.map((l) => [l.i, l]));
        return {
          ...current,
          widgets: current.widgets.map((w) => {
            const l = byId.get(w.instanceId);
            return l ? { ...w, layout: { x: l.x, y: l.y, w: l.w, h: l.h } } : w;
          }),
        };
      });
      setDirty(true);
    },
    [editing, dashboard],
  );

  const save = async () => {
    if (!dashboard) return;
    try {
      await api.put(`/api/dashboards/${dashboard.id}`, {
        name: dashboard.name,
        widgets: dashboard.widgets,
        isDefault: dashboard.isDefault,
        ownerId: dashboard.ownerId,
      });
      setDirty(false);
      setEditing(false);
      toast('Dashboard saved', 'good');
    } catch (err) {
      toast('Could not save the dashboard', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const cancel = () => {
    // Discard by reverting to the last saved copy rather than diffing — the
    // server response is the single source of truth for what "saved" means.
    if (loaded) setDashboard(loaded);
    setDirty(false);
    setEditing(false);
  };

  const addWidget = (widget: CatalogWidget) => {
    setDashboard((current) => {
      if (!current) return current;
      // Drop new widgets below everything else so nothing already placed moves.
      const bottom = current.widgets.reduce((max, w) => Math.max(max, w.layout.y + w.layout.h), 0);
      const defaults: Record<string, unknown> = {};
      for (const option of widget.options) {
        if (option.default !== undefined) defaults[option.key] = option.default;
      }
      return {
        ...current,
        widgets: [
          ...current.widgets,
          {
            instanceId: `w_${Math.random().toString(36).slice(2, 10)}`,
            widgetId: widget.fullId,
            title: null,
            layout: { x: 0, y: bottom, w: widget.defaultSize.w, h: widget.defaultSize.h },
            options: defaults,
          },
        ],
      };
    });
    setDirty(true);
    setCatalogOpen(false);
    toast(`Added "${widget.title}"`, 'good');
  };

  const removeWidget = (instanceId: string) => {
    setDashboard((current) =>
      current ? { ...current, widgets: current.widgets.filter((w) => w.instanceId !== instanceId) } : current,
    );
    setDirty(true);
  };

  if (loading && !dashboard) {
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 12 }}>
        {[2, 6, 6, 4, 4, 4].map((span, i) => (
          <Skeleton key={i} height={span === 2 ? 90 : 230} style={{ gridColumn: `span ${span === 2 ? 12 : span}` }} />
        ))}
      </div>
    );
  }

  if (error) return <ErrorState error={error} onRetry={reload} />;

  if (!dashboard) {
    return (
      <EmptyState
        icon={<LayoutGrid size={20} />}
        title="No dashboard yet"
        text="Create one to start monitoring."
        action={
          <button
            className="btn btn-primary"
            onClick={async () => {
              await api.put('/api/dashboards/dsh_new', { name: 'My dashboard', widgets: [], isDefault: true });
              reload();
            }}
          >
            Create dashboard
          </button>
        }
      />
    );
  }

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">{dashboard.name}</h1>
          <div className="page-subtitle">
            {dashboard.widgets.length} widget{dashboard.widgets.length === 1 ? '' : 's'}
            {editing && ' · drag to rearrange, drag an edge to resize'}
          </div>
        </div>

        <div className="row">
          {editing ? (
            <>
              <button className="btn" onClick={() => setCatalogOpen(true)}>
                <Plus size={15} /> Add widget
              </button>
              <button className="btn" onClick={cancel}>
                <X size={15} /> Cancel
              </button>
              <button className="btn btn-primary" onClick={save} disabled={!dirty}>
                <Check size={15} /> Save
              </button>
            </>
          ) : (
            <button className="btn" onClick={() => setEditing(true)}>
              <LayoutGrid size={15} /> Edit layout
            </button>
          )}
        </div>
      </div>

      <MyTasksPanel />

      {dashboard.widgets.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid size={20} />}
          title="This dashboard is empty"
          text="Add widgets from any installed plugin to build your view."
          action={
            <button
              className="btn btn-primary"
              onClick={() => {
                setEditing(true);
                setCatalogOpen(true);
              }}
            >
              <Plus size={15} /> Add your first widget
            </button>
          }
        />
      ) : (
        <Grid
          className="layout"
          layout={layout}
          cols={12}
          rowHeight={44}
          margin={[12, 12]}
          containerPadding={[0, 0]}
          isDraggable={editing}
          isResizable={editing}
          draggableHandle=".widget-drag-handle"
          onLayoutChange={onLayoutChange}
          // Vertical compaction keeps the wall dense as widgets are added and
          // removed; free-floating gaps waste a NOC screen.
          compactType="vertical"
          useCSSTransforms
        >
          {dashboard.widgets.map((widget) => (
            <div key={widget.instanceId}>
              <WidgetRenderer
                instance={widget as WidgetInstance}
                definition={definitions.get(widget.widgetId)}
                editing={editing}
                onRemove={() => removeWidget(widget.instanceId)}
              />
            </div>
          ))}
        </Grid>
      )}

      <Drawer open={catalogOpen} onClose={() => setCatalogOpen(false)} title="Add a widget">
        <WidgetCatalog widgets={catalog ?? []} onAdd={addWidget} />
      </Drawer>
    </>
  );
}

const TASK_PRIORITY_INTENT = { urgent: 'bad', high: 'warn', normal: 'info', low: 'neutral' } as const;

/**
 * Fixed above the widget grid rather than a widget itself — every user gets
 * their own queue regardless of who owns the dashboard's layout, and it needs
 * a create action the generic widget contract has no room for.
 */
function MyTasksPanel() {
  const { user, can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const { data, loading, reload } = useFetch(
    () => (can('task:read') ? api.get<Task[]>('/api/tasks', { userId: user?.id, state: 'open', limit: 6 }) : Promise.resolve([])),
    [user?.id],
  );
  const { data: assignees } = useFetch(
    () => (can('task:write') ? api.get<TaskAssignee[]>('/api/tasks/assignees') : Promise.resolve([])),
    [],
  );

  useRealtimeEvent(['tasks'], (event) => {
    if (event.type === 'task.created' || event.type === 'task.updated') reload();
  });

  if (!can('task:read')) return null;
  const tasks = data ?? [];

  const complete = async (id: string) => {
    try {
      await api.patch(`/api/tasks/${id}`, { state: 'done' });
      reload();
    } catch (err) {
      toast('Could not update the task', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <ListTodo size={14} />
        <span className="card-title">My tasks</span>
        <span className="card-subtitle" style={{ marginLeft: 8 }}>
          {tasks.length} open
        </span>
        <span className="grow" />
        {can('task:write') && (
          <button className="btn btn-sm" onClick={() => setCreateOpen(true)}>
            <Plus size={13} /> New task
          </button>
        )}
        <button className="btn btn-sm" onClick={() => navigate('/tasks')}>
          View all
        </button>
      </div>

      <div className="card-body">
        {loading && !data ? (
          <Skeleton height={80} />
        ) : tasks.length === 0 ? (
          <div className="row" style={{ gap: 8 }}>
            <CheckCircle2 size={16} className="subtle" />
            <span className="subtle" style={{ fontSize: 13 }}>
              Nothing on your plate.
            </span>
          </div>
        ) : (
          <div className="col" style={{ gap: 8 }}>
            {tasks.map((task) => (
              <div key={task.id} className="row" style={{ gap: 10 }}>
                <button
                  className="btn btn-sm"
                  style={{ width: 26, height: 26, padding: 0 }}
                  title="Mark done"
                  onClick={() => complete(task.id)}
                >
                  <Check size={13} />
                </button>
                <span
                  style={{ fontSize: 13.5, fontWeight: 550, cursor: can('task:write') ? 'pointer' : 'default', flex: 1 }}
                  onClick={() => can('task:write') && setEditingTask(task)}
                  title={can('task:write') ? 'Click to edit' : undefined}
                >
                  {task.title}
                </span>
                <Badge intent={TASK_PRIORITY_INTENT[task.priority]}>{task.priority}</Badge>
                {task.dueAt && (
                  <span className="subtle" style={{ fontSize: 11.5 }}>
                    due {formatRelative(task.dueAt)}
                  </span>
                )}
                {can('task:write') && (
                  <button
                    className="btn btn-sm"
                    style={{ width: 26, height: 26, padding: 0 }}
                    title="Edit task"
                    onClick={() => setEditingTask(task)}
                  >
                    <Pencil size={12} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="New task">
        <TaskForm
          assignees={assignees ?? []}
          defaultCustomerId={null}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      </Drawer>

      <Drawer open={!!editingTask} onClose={() => setEditingTask(null)} title="Edit task">
        {editingTask && (
          <TaskEditForm
            task={editingTask}
            assignees={assignees ?? []}
            onSaved={() => {
              setEditingTask(null);
              reload();
            }}
          />
        )}
      </Drawer>
    </div>
  );
}

function WidgetCatalog({ widgets, onAdd }: { widgets: CatalogWidget[]; onAdd: (w: CatalogWidget) => void }) {
  const [query, setQuery] = useState('');

  const grouped = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = widgets.filter(
      (w) => !needle || w.title.toLowerCase().includes(needle) || w.pluginId.includes(needle) || w.description.toLowerCase().includes(needle),
    );
    const groups = new Map<string, CatalogWidget[]>();
    for (const widget of filtered) {
      const list = groups.get(widget.pluginId) ?? [];
      list.push(widget);
      groups.set(widget.pluginId, list);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [widgets, query]);

  return (
    <>
      <input
        className="input"
        placeholder="Search widgets…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 16 }}
        autoFocus
      />

      {grouped.length === 0 ? (
        <EmptyState title="No widgets found" text="Try a different search, or enable more plugins." />
      ) : (
        grouped.map(([pluginId, items]) => (
          <section key={pluginId} style={{ marginBottom: 22 }}>
            <div className="nav-section-label" style={{ padding: '0 0 8px' }}>
              {pluginId}
            </div>
            <div className="col" style={{ gap: 7 }}>
              {items.map((widget) => (
                <button
                  key={widget.fullId}
                  className="card"
                  onClick={() => onAdd(widget)}
                  style={{ padding: 12, textAlign: 'left', cursor: 'pointer', background: 'var(--itr-surface)' }}
                >
                  <div className="row" style={{ gap: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 590 }}>{widget.title}</span>
                    <span className="badge" style={{ marginLeft: 'auto' }}>
                      {widget.type}
                    </span>
                  </div>
                  {widget.description && (
                    <div className="subtle" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.45 }}>
                      {widget.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </section>
        ))
      )}
    </>
  );
}
